import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePageSEO } from "@/hooks/usePageSEO";
import { FOUNDER } from "@/data/founderEntity";
import {
  ArrowLeft, Mic, ExternalLink, Award, Download, Mail,
  Youtube, Radio, FileText
} from "lucide-react";

export const PressPage = () => {
  usePageSEO(
    "Jamilah N. Lawry Press, Media & Appearances",
    "Explore interviews, press coverage, media appearances, articles, and speaking engagements featuring Jamilah N. Lawry, professionally known as Nina Capone."
  );

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": FOUNDER.domain },
      { "@type": "ListItem", "position": 2, "name": "Press & Media", "item": `${FOUNDER.domain}/press` }
    ]
  };

  // Verified podcast appearances
  const podcastAppearances = [
    { title: "10 Minutes of Talk", platform: "Spreaker / Apple Podcasts / Spotify", role: "Host", url: "https://www.spreaker.com/podcast/10-minutes-of-talk--5933432", description: "Business insights podcast hosted by Jamilah N. Lawry (as Nina Capone), covering entrepreneurship, strategy, and growth." },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="press-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" />
          </Link>
          <Link to="/" className="text-neutral-300 hover:text-white text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-5xl mx-auto px-4 py-3">
        <ol className="flex items-center gap-2 text-xs text-neutral-500">
          <li><Link to="/" className="hover:text-neutral-900">Home</Link></li>
          <li>/</li>
          <li className="text-neutral-900 font-medium" aria-current="page">Press & Media</li>
        </ol>
      </nav>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2">Press, Media & Appearances</h1>
        <p className="text-neutral-600 mb-8">
          Media coverage, interviews, podcast appearances, and speaking engagements featuring{" "}
          <Link to="/jamilah-n-lawry" className="text-[#2BC40E] hover:underline">{FOUNDER.primaryName}</Link>,
          professionally known as {FOUNDER.entertainmentName}.
        </p>

        {/* Podcasts */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Mic className="w-5 h-5 text-[#2BC40E]" /> Podcast
          </h2>
          <div className="space-y-3">
            {podcastAppearances.map(item => (
              <Card key={item.title} className="border-neutral-200">
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#333333] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Radio className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-neutral-900">{item.title}</h3>
                      <Badge variant="outline" className="text-xs">{item.role}</Badge>
                    </div>
                    <p className="text-sm text-neutral-600 mb-1">{item.description}</p>
                    <p className="text-xs text-neutral-500">{item.platform}</p>
                  </div>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <Button size="sm" variant="outline" className="flex-shrink-0"><ExternalLink className="w-3.5 h-3.5 mr-1" /> Listen</Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Awards */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-[#2BC40E]" /> Awards & Recognition
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {FOUNDER.awards.map(award => (
              <a key={award.name} href={award.url} target="_blank" rel="noopener noreferrer"
                className="p-4 bg-amber-50 rounded-xl border border-amber-200 hover:shadow-md transition-all group flex items-center gap-3">
                <Award className="w-8 h-8 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-sm text-neutral-900 group-hover:text-amber-700 flex items-center gap-1">{award.name} <ExternalLink className="w-3 h-3 opacity-40" /></p>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* Media Inquiries */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#2BC40E]" /> Media Inquiries
          </h2>
          <Card className="border-neutral-200 bg-neutral-50">
            <CardContent className="p-6">
              <p className="text-neutral-700 mb-4">
                For press inquiries, interview requests, speaking engagements, or media collaboration, contact:
              </p>
              <div className="space-y-2 text-sm">
                <a href={`mailto:${FOUNDER.contact.email}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#2BC40E]">
                  <Mail className="w-4 h-4" /> {FOUNDER.contact.email}
                </a>
                <a href={`tel:${FOUNDER.contact.phone}`} className="flex items-center gap-2 text-neutral-700 hover:text-[#2BC40E]">
                  <Mic className="w-4 h-4" /> {FOUNDER.contact.phone}
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <p className="text-xs text-neutral-500">
                  <Link to="/jamilah-n-lawry" className="text-[#2BC40E] hover:underline">View {FOUNDER.primaryName}'s full profile</Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default PressPage;
